import { gql, useLazyQuery } from "@apollo/client";
import styled from "@emotion/styled";
import {
    Card,
    Collapse,
    Fade,
    Grid,
    Icon,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Tab,
    Tabs,
    Tooltip,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { getUproColor } from "utils/colors";
import CardSelection from "./components/CardSelection";
import rgba from "assets/theme/functions/rgba";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    overflowY: "auto",
    p: 3,
    transition: "width 0.5s",
};

export default function ModalNewProducto({
    open,
    handleClose,
    optionsCreate,
    openModalProducto = () => { },
    openModalComercial = () => { },
}) {
    const [animatedIcon, setAnimatedIcon] = useState({
        tipo: "",
        elaboracion: "",
    });
    const [step, setStep] = useState(0);
    const [selectedOption, setSelectedOption] = useState({
        tipo: "",
        elaboracion: "",
    });

    useEffect(() => {
        if (open) {
            setStep(0);
            setSelectedOption({
                tipo: "",
                elaboracion: "",
            });
        }
    }, [open]);

    const handleClick = (prodSelection) => {
        setAnimatedIcon(prodSelection);
        if (selectedOption === prodSelection) {
            setSelectedOption();
        } else {
            setSelectedOption(prodSelection);
        }

        setTimeout(() => setAnimatedIcon(""), 1000);
    };

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={open}
            onClose={handleClose}
            closeAfterTransition
        >
            <Fade in={open}>
                <Card sx={{ ...style, width: { xs: "90%", sm: "90%", xxl: step === 2 ? "70%" : "30%" } }}>
                    <SoftBox>
                        <SoftBox display="flex" justifyContent="space-between">
                            <SoftBox>
                                <SoftTypography variant="h6">{`${step === 0
                                    ? "Selecioná una opción"
                                    : "¿Requiere elaboración previa?"
                                    }`}</SoftTypography>
                            </SoftBox>
                            <SoftBox display="flex" justifyContent="space-between">
                                <SoftBox>
                                    <Tooltip title="Cerrar" placement="top">
                                        <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                                            close
                                        </Icon>
                                    </Tooltip>
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                        <SoftBox pt={3}>
                            <SoftBox
                                display="flex"
                                flexDirection={{
                                    xs: "column",
                                    sm: "row",
                                }}
                                justifyContent="space-between"
                                gap={2}
                            >
                                <SoftBox sx={{ width: "100%" }}>
                                    <Collapse in={step === 0} unmountOnExit mountOnEnter>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <CardSelection
                                                    handleClick={() => handleClick({ tipo: "receta" })}
                                                    animatedIcon={animatedIcon.tipo === "receta"}
                                                    iconName="assignment"
                                                    title="Producto con receta"
                                                    subtitle="Seleccione esta opcion si quiere agregar un producto que tenga ingredientes y que puede o no requerir elaboración previa. Por ejemplo: Bifes, Pizzas, etc."
                                                    backGround={selectedOption.tipo === "receta" ? rgba(getUproColor("uproYellow"), 0.5) : "rgba(248, 246, 243, 1)"}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <CardSelection
                                                    handleClick={() => handleClick({ tipo: "ingrediente" })}
                                                    animatedIcon={animatedIcon.tipo === "ingrediente"}
                                                    iconName="flatware"
                                                    title="Producto como ingrediente"
                                                    subtitle="Seleccione esta opción si quiere agregar un producto que será utilizado como ingrediente en una receta para la elaboración de otro producto. Por ejemplo: Base de tarta. Calculada para una sola unidad, es decir, por ejemplo: para una sola tarta."
                                                    backGround={selectedOption.tipo === "ingrediente" ? rgba(getUproColor("uproYellow"), 0.5) : "rgba(248, 246, 243, 1)"}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <CardSelection
                                                    handleClick={() => handleClick({ tipo: "venta" })}
                                                    animatedIcon={animatedIcon.tipo === "venta"}
                                                    iconName="dinner_dining"
                                                    title="Producto para venta directa"
                                                    subtitle="Seleccione esta opción si quiere agregar un producto que será vendido directamente al cliente"
                                                    backGround={selectedOption.tipo === "venta" ? rgba(getUproColor("uproYellow"), 0.5) : "rgba(248, 246, 243, 1)"}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Collapse>
                                    <Collapse in={step === 1 && selectedOption.tipo === "receta"} unmountOnExit mountOnEnter>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <CardSelection
                                                    handleClick={() => handleClick((prev) => ({ ...prev, elaboracion: "elaborado" }))}
                                                    animatedIcon={animatedIcon.elaboracion === "elaborado"}
                                                    iconName="precision_manufacturing"
                                                    title="Con elaboración previa"
                                                    subtitle="Seleccione esta opción el producto requiere ser elaborado antes de ser vendido. Tener en cuenta que debe tener stock disponible al momento de su venta (Inventario de Platos). Por ejemplo: una torta"
                                                    backGround={selectedOption.elaboracion === "elaborado" ? rgba(getUproColor("uproYellow"), 0.5) : "rgba(248, 246, 243, 1)"}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <CardSelection
                                                    handleClick={() => handleClick((prev) => ({ ...prev, elaboracion: "noElaborado" }))}
                                                    animatedIcon={animatedIcon.elaboracion === "noElaborado"}
                                                    iconName="sell"
                                                    title="Elaboración al momento de la venta"
                                                    subtitle="Seleccione esta opción si el producto se elabora en el momento que es vendido. Por ejemplo: café"
                                                    backGround={selectedOption.elaboracion === "noElaborado" ? rgba(getUproColor("uproYellow"), 0.5) : "rgba(248, 246, 243, 1)"}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Collapse>
                                    <SoftBox
                                        mt={2}
                                        display="flex"
                                        justifyContent={step > 0 ? "space-between" : "flex-end"}
                                        gap={2}
                                    >
                                        <Collapse in={step > 0} unmountOnExit mountOnEnter>
                                            <SoftButton
                                                color="primary"
                                                circular
                                                onClick={() => {
                                                    setStep(step - 1);
                                                }}
                                                fullWidth
                                            >
                                                <Icon>arrow_back</Icon>&nbsp;Volver
                                            </SoftButton>
                                        </Collapse>
                                        <Collapse in={step < 2} unmountOnExit mountOnEnter>
                                            <SoftButton
                                                color="uproGreen"
                                                circular
                                                onClick={() => {
                                                    if (selectedOption.tipo === "venta") {
                                                        openModalComercial();
                                                        handleClose();
                                                        return;
                                                    }
                                                    if ((step === 1 && selectedOption.tipo === "receta") || selectedOption.tipo !== "receta") {
                                                        openModalProducto();
                                                        optionsCreate({
                                                            esElaborado: selectedOption.tipo === "ingrediente" ? true : selectedOption.elaboracion,
                                                            esIngrediente: selectedOption.tipo === "ingrediente",
                                                        });
                                                        handleClose();
                                                    } else {
                                                        setStep(step + 1);
                                                    }
                                                }}
                                                fullWidth
                                            >
                                                Siguiente&nbsp;
                                                <Icon>arrow_forward</Icon>
                                            </SoftButton>
                                        </Collapse>
                                    </SoftBox>
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                    </SoftBox>
                </Card>
            </Fade>
        </Modal>
    );
}

ModalNewProducto.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    refetch: PropTypes.func,
};
